import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { KYCQueuePage } from './pages/KYCQueuePage';
import { ModerationQueue } from './components/ModerationQueue';
import { SettingsPage } from './pages/SettingsPage';
import { HelpDocsPage } from './pages/HelpDocsPage';

import {
  isAdminSessionInactive,
  clearAdminSession,
} from './utils/api';

// ── Auth Guard ──────────────────────────────────────────────────────────────────
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const isAuthed = sessionStorage.getItem('bo_authed') === '1';
  const isInactive = isAuthed && isAdminSessionInactive();

  useEffect(() => {
    if (!isAuthed) {
      navigate('/login', { replace: true });
    } else if (isInactive) {
      clearAdminSession('inactivity');
      navigate('/login?reason=inactivity', { replace: true });
    }
  }, [isAuthed, isInactive, navigate]);

  if (!isAuthed || isInactive) return null;
  return <Layout>{children}</Layout>;
};

// ── Redirect root to dashboard or login ─────────────────────────────────────────
const RootRedirect: React.FC = () => {
  const isAuthed = sessionStorage.getItem('bo_authed') === '1';
  return <Navigate to={isAuthed ? '/dashboard' : '/login'} replace />;
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<RootRedirect />} />

        {/* Protected Admin Routes */}
        <Route path="/dashboard"     element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/users"         element={<PrivateRoute><UsersPage /></PrivateRoute>} />
        <Route path="/users/:id"     element={<PrivateRoute><UserProfilePage /></PrivateRoute>} />
        <Route path="/kyc"           element={<PrivateRoute><KYCQueuePage /></PrivateRoute>} />
        <Route path="/moderation"    element={<PrivateRoute><ModerationQueue /></PrivateRoute>} />
        <Route path="/subscriptions" element={<PrivateRoute><SubscriptionsPage /></PrivateRoute>} />
        <Route path="/payments"      element={<PrivateRoute><PaymentsPage /></PrivateRoute>} />
        <Route path="/settings"      element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/help"          element={<PrivateRoute><HelpDocsPage /></PrivateRoute>} />
        <Route path="/docs"          element={<PrivateRoute><HelpDocsPage /></PrivateRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
